import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'flowbite-react';
import { Field, Form, Formik } from 'formik';
import moment from 'moment';
import { Client, Project, Track } from '../../models';
import {
    createTrack,
    loadActiveTrack,
    loadProjects,
    RootState,
    useAppDispatch,
    useAppSelector,
} from '../../redux';
import {
    Button, ClientSelectField, Icon, SelectField, ToggleField,
} from '..';
import { projectSelectOptions } from '../../utils/projectSelectOptions';

export type QuickTrackModalProps = {
    show: boolean;
    onClose?: () => void;
    onAdded?: (track: Track) => void;
    /** Prefill / force the billing client on create. */
    initialClient?: Client;
};

function QuickTrackModal(props: QuickTrackModalProps) {
    const { show, onClose, onAdded, initialClient } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { projects } = useAppSelector((state: RootState) => state.projects);
    const { user } = useAppSelector((state: RootState) => state.auth);
    const { creating } = useAppSelector((state: RootState) => state.tracks);

    const [selectedClient, setSelectedClient] = useState<Client | undefined>(initialClient);
    const [clientError, setClientError] = useState('');
    const [projectError, setProjectError] = useState('');
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        if (show)
            dispatch(loadProjects());
    }, [dispatch, show]);

    useEffect(() => {
        if (!show)
            return;
        setSelectedClient(initialClient);
        setClientError('');
        setProjectError('');
        setSubmitError('');
    }, [show, initialClient]);

    const defaultProjectId = user?.trackingPreferences?.defaultProjectId || '';
    const preferredBillingMode = user?.trackingPreferences?.defaultBillingMode || 'hourly';

    const projectOptions = useMemo(
        () => projectSelectOptions(projects, selectedClient?._id, defaultProjectId || undefined),
        [projects, selectedClient, defaultProjectId]
    );

    const initialValues = useMemo(() => ({
        client: initialClient,
        projectId: defaultProjectId || '',
        title: '',
        description: '',
        billingMode: preferredBillingMode === 'floor' ? 'hourly' : preferredBillingMode,
        billingDev: false,
        startAtTime: moment().format('HH:mm'),
    }), [initialClient, defaultProjectId, preferredBillingMode, show]);

    const billingModeOptions = [
        { value: 'hourly', label: t('tracks.billing_mode_hourly') },
        { value: 'package', label: t('tracks.billing_mode_package') },
        { value: 'free', label: t('tracks.billing_mode_free') },
    ];

    const submit = (values: AnyObj) => {
        const client = values.client || selectedClient || initialClient;
        const clientId = client?._id || (typeof client === 'string' ? client : undefined);
        if (!clientId) {
            setClientError(t('tracks.select_client') || '');
            return;
        }
        if (!values.projectId) {
            setProjectError(t('tracks.select_project') || '');
            return;
        }

        setClientError('');
        setProjectError('');
        setSubmitError('');

        const project = projects.find((p: Project) => p._id === values.projectId);
        if (!project) {
            setProjectError(t('tracks.select_project') || '');
            return;
        }

        const [hours, minutes] = String(values.startAtTime || '').split(':');
        let startAt = moment()
            .hours(parseInt(hours, 10) || 0)
            .minutes(parseInt(minutes, 10) || 0)
            .seconds(0)
            .milliseconds(0);

        // Clamp small clock/skew drift so "now" from the time field is not rejected.
        const now = moment();
        if (startAt.isAfter(now) && startAt.diff(now, 'seconds') <= 120)
            startAt = now;

        const billingMode = values.billingMode || 'hourly';
        // Running timer: no stopAt — becomes the active track.
        const properties: AnyObj = {
            title: values.title,
            description: values.description || '',
            project,
            client,
            startAt,
            billingMode,
            billingDev: billingMode === 'free' ? false : !!values.billingDev,
        };

        dispatch(createTrack(properties, {}, (err: Error | null, track?: Track) => {
            if (err) {
                setSubmitError(err.message || t('tracks.start_failed') || '');
                return;
            }
            dispatch(loadActiveTrack());
            if (track && onAdded)
                onAdded(track);
            else if (onClose)
                onClose();
        }));
    };

    if (!show)
        return null;

    return (
        <Modal show={show} size="2xl" dismissible onClose={onClose}>
            <Modal.Header>
                <div className="flex items-center gap-x-2 min-w-0">
                    <Icon type="timer" size={5} className="shrink-0" />
                    <span className="truncate">{t('client_view.new_track')}</span>
                </div>
            </Modal.Header>
            <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={submit}
            >
                {({ values, setFieldValue, isSubmitting }) => (
                    <Form>
                        <Modal.Body>
                            <div className="flex flex-col gap-y-5">
                                <div className="flex flex-col gap-y-1">
                                    <label className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                        {t('tracks.client')}
                                    </label>
                                    <ClientSelectField
                                        name="client"
                                        placeholder={t('tracks.select_client') || ''}
                                        allowClear={!initialClient}
                                        value={selectedClient}
                                        onChange={(client?: Client) => {
                                            setSelectedClient(client);
                                            setFieldValue('client', client || null, false);
                                            setFieldValue('projectId', '', false);
                                            setClientError('');
                                        }}
                                    />
                                    {clientError && (
                                        <p className="text-xs text-red-600">{clientError}</p>
                                    )}
                                </div>

                                <div className="flex flex-col gap-y-1">
                                    <label className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                        {t('tracks.project')}
                                    </label>
                                    <SelectField
                                        name="projectId"
                                        placeholder={t('tracks.select_project') || ''}
                                        options={projectOptions}
                                        onChange={() => setProjectError('')}
                                    />
                                    {projectError && (
                                        <p className="text-xs text-red-600">{projectError}</p>
                                    )}
                                </div>

                                <div className="flex flex-col gap-y-1">
                                    <label className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                        {t('tracks.title')}
                                    </label>
                                    <Field
                                        type="text"
                                        name="title"
                                        required
                                        autoFocus
                                        placeholder={t('tracks.title_placeholder') || ''}
                                        className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                                    />
                                </div>

                                <div className="flex flex-col gap-y-1">
                                    <label className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                        {t('tracks.description')}
                                    </label>
                                    <Field
                                        as="textarea"
                                        name="description"
                                        rows={3}
                                        placeholder={t('tracks.description_placeholder') || ''}
                                        className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                                    />
                                </div>

                                <div className="flex flex-col gap-y-2">
                                    <span className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                        {t('tracks.billing')}
                                    </span>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-md border border-gray-200 dark:border-gray-600 p-3">
                                        <div className="flex-1 min-w-[12rem]">
                                            <SelectField
                                                name="billingMode"
                                                options={billingModeOptions}
                                                onChange={(mode: string) => {
                                                    if (mode === 'free')
                                                        setFieldValue('billingDev', false);
                                                }}
                                            />
                                        </div>
                                        {values.billingMode !== 'free' && (
                                            <div className="flex items-center justify-between gap-x-3 shrink-0">
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                                    {t('tracks.billing_dev')}
                                                </span>
                                                <ToggleField name="billingDev" />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex flex-col gap-y-2 items-center rounded-md border border-gray-200 dark:border-gray-600 p-3 sm:w-fit">
                                    <span className="text-sm font-bold">{t('tracks.start')}</span>
                                    <Field
                                        type="time"
                                        name="startAtTime"
                                        required
                                        className="block focus:ring-0 border-0 border-b-2 border-gray-300 dark:border-gray-500 py-1.5 shadow-sm font-bold text-sm w-fit bg-transparent"
                                    />
                                </div>

                                {submitError && (
                                    <p className="text-sm text-red-600" role="alert">{submitError}</p>
                                )}
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <div className="flex w-full justify-end gap-3">
                                <Button
                                    type="button"
                                    color="default"
                                    title={t('common.cancel') || ''}
                                    onClick={onClose}
                                />
                                <Button
                                    type="submit"
                                    color="primary"
                                    icon="start"
                                    title={t('client_view.start_track') || t('tracks.start') || ''}
                                    loading={creating === 'pending' || isSubmitting}
                                />
                            </div>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
}

export default QuickTrackModal;
